/**
 * Visitors by country as a choropleth. One sequential hue (light = few, dark = many), five
 * bins, countries without visitors in the neutral tile colour. Hover shows the exact number;
 * clicking a country filters the dashboard to it.
 */
import { geoNaturalEarth1, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { numericToAlpha2 } from "i18n-iso-countries";
import type { FeatureCollection, Geometry } from "geojson";
import type { Topology } from "topojson-specification";
import { assetUrl } from "./share";

// Sequential blue, steps 150/300/450/550/650 of the dataviz reference ramp (lightness falls monotonically).
export const MAP_RAMP = ["#b7d3f6", "#6da7ec", "#2a78d6", "#1c5cab", "#104281"];
const NO_DATA = "var(--tile)";

let world: Promise<FeatureCollection<Geometry, { name: string }>> | null = null;
function loadWorld() {
  world ??= fetch(assetUrl("/countries-110m.json"))
    .then((r) => r.json())
    .then((t: Topology) => feature(t, t.objects.countries!) as unknown as FeatureCollection<Geometry, { name: string }>);
  return world;
}

/** Bin edges as fractions of the busiest country; most sites have a long tail, so the low bins are narrow. */
const EDGES = [0, 0.05, 0.15, 0.35, 0.65];

export async function countryMap(
  host: HTMLElement,
  visitorsByCountry: Map<string, number>,
  opts: { tooltip: HTMLElement; name: (code: string) => string; onPick: (code: string) => void; format: (n: number) => string },
): Promise<void> {
  const geo = await loadWorld();
  const max = Math.max(1, ...visitorsByCountry.values());
  const bin = (v: number) => {
    let i = 0;
    for (let b = 0; b < EDGES.length; b++) if (v / max > EDGES[b]!) i = b;
    return i;
  };

  const W = host.clientWidth || 600;
  const H = Math.round(W * 0.5);
  const features = { ...geo, features: geo.features.filter((f) => f.properties.name !== "Antarctica") };
  const projection = geoNaturalEarth1().fitSize([W, H], features);
  const path = geoPath(projection);

  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", "Visitors by country");
  svg.style.width = "100%";
  svg.style.height = "auto";

  for (const f of features.features) {
    const code = numericToAlpha2(String(f.id)) ?? "";
    const v = visitorsByCountry.get(code) ?? 0;
    const p = document.createElementNS(NS, "path");
    p.setAttribute("d", path(f) ?? "");
    p.setAttribute("fill", v > 0 ? MAP_RAMP[bin(v)]! : NO_DATA);
    // 1px surface-coloured borders keep neighbours apart without drawing attention.
    p.setAttribute("stroke", "var(--card)");
    p.setAttribute("stroke-width", "0.75");
    if (v > 0) {
      p.style.cursor = "pointer";
      p.addEventListener("click", () => opts.onPick(code));
    }
    p.addEventListener("pointermove", (ev) => {
      const tip = opts.tooltip;
      tip.innerHTML = `<b>${opts.name(code) || f.properties.name}</b><div>${v ? `${opts.format(v)} visitors` : "No visitors"}</div>`;
      tip.hidden = false;
      tip.style.left = `${Math.min(ev.clientX + 14, innerWidth - tip.offsetWidth - 8)}px`;
      tip.style.top = `${ev.clientY - tip.offsetHeight - 10}px`;
      p.setAttribute("stroke", "var(--ink)");
      p.parentNode?.appendChild(p); // draw the hovered outline on top
    });
    p.addEventListener("pointerleave", () => {
      opts.tooltip.hidden = true;
      p.setAttribute("stroke", "var(--card)");
    });
    svg.append(p);
  }

  // Legend: the five bins with their visitor ranges, plus "none".
  const legend = document.createElement("div");
  legend.className = "map-legend";
  // Bin i holds values v with EDGES[i]·max < v ≤ EDGES[i+1]·max; empty bins are left out.
  const lo = (i: number) => Math.floor(EDGES[i]! * max) + 1;
  const hi = (i: number) => (i < EDGES.length - 1 ? Math.floor(EDGES[i + 1]! * max) : max);
  legend.innerHTML =
    `<span><i style="background:${NO_DATA}"></i>0</span>` +
    MAP_RAMP.map((c, i) =>
      lo(i) > hi(i) ? "" : `<span><i style="background:${c}"></i>${lo(i) === hi(i) ? opts.format(lo(i)) : `${opts.format(lo(i))}–${opts.format(hi(i))}`}</span>`,
    ).join("");
  host.replaceChildren(svg, legend);
}
